import { ScreenOverlay } from 'components/ScreenOverlay';
import { Txt } from 'components/Txt';
import { useSelector } from 'hooks/useSelector';
import { APP_VERSION } from 'lib/getDeviceInformation';
import { theme } from 'lib/theme';
import * as React from 'react';
import {
  ImageBackground,
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  StyleSheet,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  selectAuthError,
  selectHasAuthToken,
  selectPromptAuth,
} from 'store/auth/selectors';
import { shouldRedirectWeb } from 'lib/riskbase';
import { ActivityIndicator } from 'components/BlockingLoader/ActivityIndicator';
import {
  CHECK_FOR_APP_UPDATE_MANUAL,
  doAuthHideAuthModal,
} from 'store/actions';
import { useDispatch } from 'react-redux';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import { Button, ButtonTheme } from 'components/Button';
import { As } from './As';

import { AuthPageSwitcher } from './AuthPageSwitcher';
import { NativeBanner } from './NativeBanner';

const AssetBackdropJpeg = require('assets/backdrop.jpg');

const WithKeyboardAvoidingView: React.FC<{
  children: React.ReactNode;
  withAvoidance: boolean;
}> = ({ children, withAvoidance }) =>
  withAvoidance ? (
    <KeyboardAvoidingView behavior="padding" style={{ flex: 1 }}>
      {children}
    </KeyboardAvoidingView>
  ) : (
    <>{children}</>
  );

const INIT_BACKGROUND_OPACITY = 0;
const INIT_FORM_OPACITY = 0;
const INIT_FORM_SCALE = 0.9;
const INIT_FORM_Y = 20;

export const AuthModal: React.FC = () => {
  const dispatch = useDispatch();
  const hasAuthorizationToken = useSelector(selectHasAuthToken);
  const visible = !!useSelector(selectPromptAuth);
  const error = useSelector(selectAuthError);
  const { top, bottom, left, right } = useSafeAreaInsets();
  const redirectTo = hasAuthorizationToken ? false : shouldRedirectWeb();
  const isOutOfDate =
    error === 'ClientOutOfDateMinor' || error === 'ClientOutOfDateMajor';

  React.useEffect(() => {
    if (!redirectTo && isOutOfDate) {
      dispatch(doAuthHideAuthModal());
    }
  }, [dispatch, isOutOfDate, redirectTo]);

  const opacityBackdropSV = useSharedValue(INIT_BACKGROUND_OPACITY);
  const opacitySV = useSharedValue(INIT_FORM_OPACITY);
  const ySV = useSharedValue(INIT_FORM_Y);
  const scaleSV = useSharedValue(INIT_FORM_SCALE);

  const animatedStyles = useAnimatedStyle(() => {
    return {
      opacity: opacitySV.value,
      transform: [{ translateY: ySV.value }, { scale: scaleSV.value }],
    };
  });

  const backdropStyles = useAnimatedStyle(() => {
    return {
      opacity: opacityBackdropSV.value,
    };
  });

  React.useEffect(() => {
    if (visible) {
      opacityBackdropSV.value = withDelay(
        100,
        withTiming(1, {
          easing: Easing.inOut(Easing.poly(4)),
          duration: 500,
        }),
      );
      scaleSV.value = withDelay(250, withSpring(1));
      ySV.value = withDelay(250, withSpring(0));
      opacitySV.value = withDelay(
        250,
        withTiming(1, {
          easing: Easing.inOut(Easing.poly(4)),
        }),
      );
    }
    return () => {
      opacityBackdropSV.value = INIT_BACKGROUND_OPACITY;
      opacitySV.value = INIT_FORM_OPACITY;
      scaleSV.value = INIT_FORM_SCALE;
      ySV.value = INIT_FORM_Y;
    };
  }, [opacityBackdropSV, opacitySV, scaleSV, ySV, visible]);

  if (!visible) {
    return null;
  }

  return (
    <ScreenOverlay>
      <Animated.View
        style={[
          {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'white',
          },
          backdropStyles,
        ]}
      >
        <ImageBackground
          style={{
            width: '100%',
            height: '100%',
          }}
          imageStyle={{ resizeMode: 'cover', opacity: 0.2 }}
          source={AssetBackdropJpeg}
        />
      </Animated.View>
      <Animated.View style={[StyleSheet.absoluteFill, animatedStyles]}>
        <WithKeyboardAvoidingView withAvoidance={Platform.OS === 'ios'}>
          <Pressable
            style={[
              StyleSheet.absoluteFill,
              /** @ts-expect-error cursor exists in react-native-web */
              Platform.OS === 'web' ? { cursor: 'default' } : undefined,
            ]}
            onPress={Platform.OS !== 'web' ? Keyboard.dismiss : undefined}
          />
          <View style={styles.wrapper}>
            <View style={styles.dialogue}>
              {redirectTo !== false ? (
                <>
                  <ActivityIndicator
                    size={60}
                    style={{ marginBottom: theme.paddingHorizontal }}
                  />
                  <Txt size="large" style={{ textAlign: 'center' }}>
                    Please Wait...
                  </Txt>
                </>
              ) : (
                <>
                  <AuthPageSwitcher />
                  <As />
                </>
              )}
            </View>
          </View>
        </WithKeyboardAvoidingView>
      </Animated.View>
      {!hasAuthorizationToken &&
        (Platform.OS === 'web' ? (
          <NativeBanner />
        ) : (
          <View
            style={{
              position: 'absolute',
              bottom: bottom + theme.paddingHorizontal * 2,
              left: left + theme.paddingHorizontal * 2,
              right: right + theme.paddingHorizontal * 2,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Button
              text="Check for Updates"
              onPress={() => dispatch(CHECK_FOR_APP_UPDATE_MANUAL())}
              buttonTheme={ButtonTheme.themeless}
              compact
            />
          </View>
        ))}
      <View style={[styles.appVersion, { top: top + theme.paddingHorizontal }]}>
        <Txt size="xsmall" weight="bold">
          V{APP_VERSION}
        </Txt>
      </View>
    </ScreenOverlay>
  );
};

const styles = StyleSheet.create({
  dialogue: {
    maxWidth: '100%',
    width: 400,
    maxHeight: '80%',
    paddingVertical: theme.paddingVertical,
    paddingHorizontal: theme.paddingVertical,
  },
  wrapper: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  appVersion: {
    position: 'absolute',
    alignSelf: 'center',
    opacity: 0.2,
    backgroundColor: 'rgba(0,0,0,.05)',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
});
